import { GraphQLClient } from 'graphql-request';
import { getSdk } from './graphql-sdk';
import type { PreviewMode } from '@/contexts/SiteContext';

// Cache GraphQL clients by preview mode to avoid recreating
const clientCache = new Map<string, GraphQLClient>();

export class GraphQLRequester {
    private client: GraphQLClient;
    private sdk: ReturnType<typeof getSdk>;

    constructor(
        endpoint: string = process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT || 'https://comedyclubhaug.ddev.site/api',
        token: string = process.env.NEXT_PUBLIC_GRAPHQL_TOKEN || '',
        previewMode: PreviewMode = { mode: "none", code: null, token: null }
    ) {
        // Use cached client if available
        const cacheKey = `${previewMode.mode}-${previewMode.code || 'none'}`;
        let client = clientCache.get(cacheKey);
        
        if (!client) {
            const url = new URL(endpoint);
            
            // Add preview parameters if needed
            if (previewMode.mode !== "none" && previewMode.code) {
                const param = previewMode.mode === "live" ? "x-craft-live-preview" : "x-craft-preview";
                url.searchParams.set(param, previewMode.code);
                if (previewMode.token) {
                    url.searchParams.set("token", previewMode.token);
                }
            }

            client = new GraphQLClient(url.toString(), {
                headers: {
                    Authorization: `Bearer ${token}`,
                    ...(previewMode.mode !== "none" ? { "x-craft-gql-cache": "no-cache" } : {}),
                },
                // Next.js 16: Explicit cache configuration required
                // Preview mode: Disable caching to ensure fresh preview content
                // Production mode: Use undefined to allow default caching behavior
                cache: previewMode.mode !== "none" ? "no-store" : undefined,
            });
            
            clientCache.set(cacheKey, client);
        }
        
        this.client = client;
        this.sdk = getSdk(this.client);
    }

    // Execute SDK method with error handling
    async executeSDKMethod(methodName: string, variables?: any): Promise<any> {
        if (!(methodName in this.sdk)) {
            throw new Error(`Method "${methodName}" not found in SDK`);
        }

        try {
            const method = this.sdk[methodName as keyof typeof this.sdk];
            return await method(variables);
        } catch (error) {
            throw new Error(`GraphQL request failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    getSdk() { return this.sdk; }
    getClient() { return this.client; }

    // Clear cache when needed
    static clearCache() { clientCache.clear(); }
}

